import React from "react";
import {
  PlxsChip,
  PlxsText,
  PlxsButton,
  PlxsTooltip,
  PlxsIcon,
} from "@plexlogic/plexus-ui";
import { useHistory } from "react-router-dom";
import { DrawResponse } from "../../services/api";
import { getStatusConfig } from "../../utils/actionRegistry";
import { formatDateTime } from "../../utils/dateTimeUtils";
import { getTruncatedDisplay } from "../../utils/textUtils";
import "./DrawCard.css";

interface DrawCardProps {
  draw: DrawResponse;
  onDelete: (event: React.MouseEvent<HTMLElement>) => void;
  onViewActivity: (drawId: string) => void;
}

const DrawCard: React.FC<DrawCardProps> = ({
  draw,
  onDelete,
  onViewActivity,
}) => {
  const history = useHistory();

  const formatDate = (dateString: string) => {
    // Use centralized datetime formatting (browser's local timezone)
    // Display without timezone abbreviation for cleaner UI
    const { date, time } = formatDateTime(dateString);
    return `${date} ${time}`;
  };

  const handleCardClick = () => {
    history.push(`/draws/${draw.draw_id}`);
  };

  const handleViewDetails = (e: React.MouseEvent) => {
    e.stopPropagation();
    history.push(`/draws/${draw.draw_id}`);
  };

  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    onDelete(e as React.MouseEvent<HTMLElement>);
  };

  const statusConfig = getStatusConfig(draw.status);
  
  // Truncate long draw names with tooltip
  const drawNameDisplay = getTruncatedDisplay(draw.draw_name, 16);

  return (
    <div 
      className="draw-card" 
      onClick={handleCardClick}
      style={{ cursor: 'pointer' }}
    >
      <div className="draw-card-header">
        <div className="draw-card-title-section">
          {drawNameDisplay.needsTooltip ? (
            <PlxsTooltip title={drawNameDisplay.fullText}>
              <div
                style={{
                  fontWeight: 600,
                  fontSize: "1.125rem",
                  color: "var(--plexus-color-neutral-100)",
                  cursor: "help",
                }}
              >
                {drawNameDisplay.displayText}
              </div>
            </PlxsTooltip>
          ) : (
            <div
              style={{
                fontWeight: 600,
                fontSize: "1.125rem",
                color: "var(--plexus-color-neutral-100)",
              }}
            >
              {drawNameDisplay.displayText}
            </div>
          )}
          <PlxsText size="sm">
            <span style={{ color: "var(--plexus-color-neutral-300)" }}>
              {draw.draw_no}
            </span>
          </PlxsText>
        </div>
        <PlxsChip
          dataTestId={`status-chip-${draw.draw_id}`}
          label={statusConfig.label}
          tone={statusConfig.tone}
          {...(statusConfig.decorativeIcon && { decorativeIcon: statusConfig.decorativeIcon as any })}
        />
      </div>

      <div className="draw-card-body">
        <div className="draw-card-info-row">
          <PlxsText size="sm">
            <span style={{ color: "var(--plexus-color-neutral-300)" }}>
              Created
            </span>
          </PlxsText>
          <PlxsText size="sm">
            <span style={{ color: "var(--plexus-color-neutral-200)" }}>
              {formatDate(draw.created_at)}
            </span>
          </PlxsText>
        </div>

        {draw.scheduled_date_time && (
          <div className="draw-card-info-row">
            <PlxsText size="sm">
              <span style={{ color: "var(--plexus-color-neutral-300)" }}>
                Scheduled
              </span>
            </PlxsText>
            <PlxsText size="sm">
              <span style={{ color: "var(--plexus-color-brand-400)" }}>
                {formatDate(draw.scheduled_date_time)}
              </span>
            </PlxsText>
          </div>
        )}

        {draw.status === "completed" && draw.updated_at && (
          <div className="draw-card-info-row">
            <PlxsText size="sm">
              <span style={{ color: "var(--plexus-color-neutral-300)" }}>
                Completed
              </span>
            </PlxsText>
            <PlxsText size="sm">
              <span style={{ color: "var(--plexus-color-positive-400)" }}>
                {formatDate(draw.updated_at)}
              </span>
            </PlxsText>
          </div>
        )}

        {draw.organisation && (
          <div className="draw-card-info-row">
            <PlxsText size="sm">
              <span style={{ color: "var(--plexus-color-neutral-300)" }}>
                Organisation
              </span>
            </PlxsText>
            <PlxsText size="sm">
              <span style={{ color: "var(--plexus-color-neutral-200)" }}>
                {draw.organisation}
              </span>
            </PlxsText>
          </div>
        )}

        {draw.status === "failed" && draw.execution_error && (
          <div className="draw-card-info-row">
            <PlxsText size="sm">
              <span style={{ color: "var(--plexus-color-neutral-300)" }}>
                Execution failed error:
              </span>
            </PlxsText>
            <PlxsTooltip title={draw.execution_error} variant="iconOnly">
              <PlxsIcon name="infoCircle" size="lg" tone="urgent" />
            </PlxsTooltip>
          </div>
        )}
      </div>

      <div
        className="draw-card-actions"
        style={{ justifyContent: "space-between", alignItems: "center" }}
      >
        <div style={{ display: "flex", gap: "var(--plexus-space-sm)" }}>
          <PlxsTooltip title="View draw details">
            <PlxsButton
              isIconOnly
              functionalIcon="eye"
              label="View details"
              onClick={handleViewDetails}
              variant="secondary"
              tone="neutral"
              size="sm"
              dataTestId={`view-details-${draw.draw_id}`}
            />
          </PlxsTooltip>
          <PlxsTooltip title="Archive draw">
            <PlxsButton
              isIconOnly
              functionalIcon="fileArchive"
              label="Archive draw"
              onClick={handleDelete}
              variant="secondary"
              tone="urgent"
              size="sm"
              dataTestId={`archive-draw-${draw.draw_id}`}
            />
          </PlxsTooltip>
        </div>
        {draw.payment_required && (
          <PlxsChip
            dataTestId={`payment-chip-${draw.draw_id}`}
            label="Payment required"
            tone="caution"
          />
        )}
      </div>
    </div>
  );
};

// Memoize component to prevent unnecessary re-renders when props haven't changed
export default React.memo(DrawCard);
